'use client';

import Box from '@enlighten/design-system/dist/Box';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import React from 'react';

import ProgramCard from './ProgramCard';
import ProgramMobileCard from './ProgramMobileCard';
import { GROUP_LABELS, PROGRAMS } from './programs';

const PANEL_ID = 'solar-support-program-detail';

const ProgramSelector = () => {
	// 데스크톱 선택 카드와 모바일 아코디언은 별도 상태 (자동 순환이 모바일 높이를 흔들지 않도록)
	const [activeIndex, setActiveIndex] = React.useState(0);
	const [openIndex, setOpenIndex] = React.useState<number | null>(0);
	const [isAutoRotating, setIsAutoRotating] = React.useState(true);
	const nextIndex = (activeIndex + 1) % PROGRAMS.length;
	const { theme, detailLabel, detail, stats, notes, groupIndex } = PROGRAMS[activeIndex];

	const handleCardSelect = (index: number) => {
		setIsAutoRotating(false);
		setActiveIndex(index);
	};

	const handleMobileCardToggle = (index: number) => {
		setIsAutoRotating(false);
		setOpenIndex(index === openIndex ? null : index);
	};

	// 다음 카드 테두리가 다 채워지는 시점이 곧 전환 시점
	const handleProgressEnd = () => setActiveIndex((previous) => (previous + 1) % PROGRAMS.length);

	return (
		<Stack width="1440px" maxWidth="100%" marginX="auto" padding={{ xs: '48px 24px 60px', md: '28px 24px 140px' }}>
			<Typography fontSize={{ xs: '24px', md: '40px' }} fontWeight={700} textAlign="center" whiteSpace={{ md: 'normal' }}>
				{'우리 회사에 맞는 사업을\n골라 보세요.'}
			</Typography>
			<Typography marginTop={{ xs: '12px', md: '20px' }} fontSize={{ xs: '16px', md: '28px' }} fontWeight={{ xs: 400, md: 500 }} color="#6F7276" textAlign="center" whiteSpace={{ md: 'normal' }}>
				{'사업마다 대상·보조율·한도를 한 카드에 담아,\n필요한 사업만 골라 읽을 수 있어요.'}
			</Typography>

			<Box display={{ xs: 'none', md: 'block' }}>
				<Stack direction="row" gap="24px" marginTop="62px">
					{GROUP_LABELS.map(({ text, width }, index) => {
						const isHighlighted = index === groupIndex;

						return (
							<Stack key={text} direction="row" alignItems="center" gap="5px" width={`${width}px`}>
								<Typography fontSize="20px" fontWeight={isHighlighted ? 700 : 400} color={isHighlighted ? theme.label : undefined} whiteSpace="nowrap">
									{text}
								</Typography>
								<Box flex={1} height="1px" bgcolor={isHighlighted ? theme.label : '#F2F3F5'} />
							</Stack>
						);
					})}
				</Stack>

				<Stack direction="row" gap="24px" marginTop="30px" paddingTop="24px" sx={{ '& > *': { flex: 1 } }}>
					{PROGRAMS.map((program, index) => (
						<ProgramCard
							key={program.number}
							program={program}
							isActive={index === activeIndex}
							isNext={isAutoRotating && index === nextIndex}
							panelId={PANEL_ID}
							onSelect={() => handleCardSelect(index)}
							onProgressEnd={handleProgressEnd}
						/>
					))}
				</Stack>

				<Stack id={PANEL_ID}>
					<Stack marginTop="24px" padding="40px" bgcolor={theme.surface} borderRadius="16px">
						<Typography fontSize="18px" fontWeight={700} color={theme.label}>
							{detailLabel}
						</Typography>
						<Stack marginTop="20px">
							{detail.map((paragraph, index) => (
								<Typography key={index} fontSize="20px">
									{paragraph}
								</Typography>
							))}
						</Stack>
					</Stack>

					<Stack direction="row" gap="24px" marginTop="24px" sx={{ '& > *': { flex: 1 } }}>
						{stats.map(({ label, value }) => (
							<Stack key={label} height="162px" justifyContent="center" padding="0 40px" border="1px solid #F2F3F5" borderRadius="16px">
								<Typography fontSize="20px" fontWeight={700}>
									{label}
								</Typography>
								<Typography marginTop="8px" fontSize="34px" fontWeight={700}>
									{value}
								</Typography>
							</Stack>
						))}
					</Stack>

					<Stack marginTop="12px">
						{notes.map((note) => (
							<Typography key={note} fontSize="20px" color="#6F7276">
								{note}
							</Typography>
						))}
					</Stack>
				</Stack>
			</Box>

			<Stack display={{ md: 'none' }} gap="24px" marginTop="32px">
				{GROUP_LABELS.map(({ text }, index) => {
					const programs = PROGRAMS.filter((program) => program.groupIndex === index);

					if (programs.length === 0) {
						return null;
					}

					const color = programs[0].theme.main;

					return (
						<Stack key={text} gap="12px">
							<Stack direction="row" alignItems="center" gap="12px">
								<Typography fontSize="16px" fontWeight={700} color={color} letterSpacing="-0.32px" whiteSpace="nowrap">
									{`${text} (${programs.length})`}
								</Typography>
								<Box flex={1} height="1px" bgcolor={color} />
							</Stack>

							{programs.map((program) => {
								const programIndex = PROGRAMS.indexOf(program);

								return (
									<ProgramMobileCard
										key={program.number}
										program={program}
										isOpen={programIndex === openIndex}
										panelId={`${PANEL_ID}-${program.number}`}
										onToggle={() => handleMobileCardToggle(programIndex)}
									/>
								);
							})}
						</Stack>
					);
				})}
			</Stack>
		</Stack>
	);
};

export default ProgramSelector;
